// rolesManager.util.ts

import { db, firebaseImport, FirebaseTimestamp, auth } from '../../config/main_config'; // Adjust the path as needed
import { createToast } from './createToast.util'; // Utility for showing toast notifications
import { closeModal } from './closeModal.util'; // Utility for closing modals
import { waitForCompanyID } from './waitForCompanyID.util';
import { openModal } from './openModal.util';
import { nameFromUID } from './nameFromUID.util';
import Tableordion from './tableordion.util';

/**
 * Interface representing a User Role.
 */
interface UserRole {
    userId: string;
    teamName: string;
    ismsRole: string;
    ismsCommittee: string;
}

/**
 * Interface representing an ISMS Role.
 */
interface ISMSRole {
    roleName: string;
    roleDescription: string;
}

/**
 * Fetches roles data from Firestore.
 * 
 * @returns A promise that resolves to an array of UserRole objects.
 */
export async function fetchRoles(): Promise<UserRole[]> {
    const companyID = await waitForCompanyID(); // Dynamic retrieval of companyID
    const docPath = `/customers/${companyID}`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        if (doc.exists) {
            const users = doc.data()?.customerOrg?.users;
            if (users && typeof users === 'object') {
                const userRoles: UserRole[] = Object.entries(users).map(([userId, user]: [string, any]) => ({
                    userId,
                    teamName: user.teamName || 'N/A',
                    ismsRole: user.ismsRole || 'N/A',
                    ismsCommittee: user.ismsCommittee || 'N/A',
                }));

                // if (autoConvertUID) {
                //     for (const userRole of userRoles) {
                //         const userName = await nameFromUID(userRole.userId, true);
                //         userRole.userId = userName;
                //     }
                // }

                console.log('Fetched user roles:', userRoles);
                return userRoles;
            } else {
                console.warn('No users data found.');
                return [];
            }
        } else {
            console.warn('Customer document does not exist.');
            return [];
        }
    } catch (error) {
        console.error('Error fetching roles:', error);
        createToast({
            message: 'Failed to load roles.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return [];
    }
}

/**
 * Fetches ISMS Roles and ISMS Committee Roles from Firestore.
 * 
 * @returns A promise that resolves to an object containing ismsRoles and ismsCommitteeRoles arrays.
 */
export async function fetchISMSRoles(): Promise<{ ismsRoles: ISMSRole[], ismsCommitteeRoles: ISMSRole[] }> {
    const companyID = await waitForCompanyID(); // Dynamic retrieval of companyID
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        if (doc.exists) {
            const ismsRolesData = doc.data()?.ismsRoles;
            const ismsCommitteeData = doc.data()?.ismsCommittee;

            const ismsRoles: ISMSRole[] = ismsRolesData
                ? Object.values(ismsRolesData).map((role: any) => ({
                    roleName: role.roleName,
                    roleDescription: role.roleDescription,
                }))
                : [];

            const ismsCommitteeRoles: ISMSRole[] = ismsCommitteeData
                ? Object.values(ismsCommitteeData).map((role: any) => ({
                    roleName: role.roleName,
                    roleDescription: role.roleDescription,
                }))
                : [];

            return { ismsRoles, ismsCommitteeRoles };
        } else {
            console.warn('ISMS Manual document does not exist.');
            return { ismsRoles: [], ismsCommitteeRoles: [] };
        }
    } catch (error) {
        console.error('Error fetching ISMS roles:', error);
        createToast({
            message: 'Failed to load ISMS roles.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return { ismsRoles: [], ismsCommitteeRoles: [] };
    }
}

/**
 * Fetches users from Firestore to populate select options.
 * 
 * @returns A promise that resolves to an array of user objects with userId and name.
 */
export async function fetchUsers(): Promise<{ userId: string, name: string }[]> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        if (doc.exists) {
            const users = doc.data()?.customerOrg?.users;
            if (users && typeof users === 'object') {
                const userList: { userId: string, name: string }[] = await Promise.all(
                    Object.keys(users).map(async (userId) => ({
                        userId,
                        name: await nameFromUID(userId)
                    }))
                );
                return userList;
            } else {
                console.warn('No users data found.');
                return [];
            }
        } else {
            console.warn('Customer document does not exist.');
            return [];
        }
    } catch (error) {
        console.error('Error fetching users:', error);
        createToast({
            message: 'Failed to load users.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return [];
    }
}

/**
 * Renders the roles into a tableordion.
 * 
 * @param roles - Array of UserRole objects.
 */
export async function renderRolesTable(roles: UserRole[]): Promise<void> {
    const tableContainer = document.querySelector('.tableordion-body');

    if (!tableContainer) {
        console.error('Roles table container not found.');
        return;
    }

    let html = ``;

    // Sort the roles by user name
    roles.sort((a, b) => {
        const nameA = a.userId.toLowerCase();
        const nameB = b.userId.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    });

    for (const role of roles) {
        html += `
            <tr data-user-id="${role.userId}" class="tableordion-row" style="pointer: default;">
                <td>${await nameFromUID(role.userId)}</td>
                <td>${role.teamName}</td>
                <td>${role.ismsRole}</td>
                <td>${role.ismsCommittee}</td>
                <td>
                    <button class="action-btn edit-role-btn" data-user-id="${role.userId}">
                        <i class="fa-duotone fa-solid fa-pencil edit-icon" alt="Edit"></i>
                    </button>
                </td>
            </tr>
        `;
    }

    tableContainer.innerHTML = html;

    // Attach event listeners to edit buttons
    attachEditButtonsListeners();
    const accordions = new Tableordion('.tableordion-row');
}

/**
 * Attaches event listeners to all edit buttons in the tableordion.
 */
function attachEditButtonsListeners(): void {
    const editButtons = document.querySelectorAll('.edit-role-btn');

    editButtons.forEach(button => {
        button.addEventListener('click', async (event) => {
            const target = event.currentTarget as HTMLElement;
            const userId = target.getAttribute('data-user-id');

            if (!userId) {
                console.error('User ID not found on the edit button.');
                return;
            }

            const roles = (window as any).roles as UserRole[];
            const userRole = roles.find(r => r.userId === userId);

            if (!userRole) {
                console.error('User role not found for the given ID.');
                return;
            }

            openEditRoleModal(userRole);
        });
    });
}

/**
 * Opens the edit role modal and populates it with current data.
 * 
 * @param userRole - The UserRole object to edit.
 */
async function openEditRoleModal(userRole: UserRole): Promise<void> {
    const modal = document.querySelector('[data-managedid="EditRoleModal"]') as HTMLElement;
    if (!modal) {
        console.error('Edit Role Modal not found.');
        return;
    }

    // Populate the modal fields
    const userSelectElement = document.getElementById('user_edit') as HTMLSelectElement;
    const teamElement = document.getElementById('team_edit') as HTMLInputElement;
    const ismsRoleElement = document.getElementById('ismsRole_edit') as HTMLSelectElement;
    const ismsCommitteeRoleElement = document.getElementById('ismsCommitteeRole_edit') as HTMLSelectElement;

    if (userSelectElement && teamElement && ismsRoleElement && ismsCommitteeRoleElement) {
        // Populate user select options
        const users = await fetchUsers();
        userSelectElement.innerHTML = users.map(user => `<option value="${user.userId}" ${user.userId === userRole.userId ? 'selected' : ''}>${user.name}</option>`).join('');

        teamElement.value = userRole.teamName;
        ismsRoleElement.value = userRole.ismsRole;
        ismsCommitteeRoleElement.value = userRole.ismsCommittee;
    } else {
        console.error('One or more modal input elements not found.');
        return;
    }

    // Store the user ID in the modal for reference during save
    modal.setAttribute('data-user-id', userRole.userId);

    // Open the modal using your existing modal management function
    openModal('[data-managedid="EditRoleModal"]');
}

/**
 * Adds or updates a user's roles in Firestore.
 * 
 * @param userId - The ID of the user to update.
 * @param team - The team name.
 * @param ismsRole - The ISMS Role.
 * @param ismsCommitteeRole - The ISMS Committee Role.
 */
export async function updateUserRoles(userId: string, team: string, ismsRole: string, ismsCommitteeRole: string): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}`;
    const docRef = db.doc(docPath);

    try {
        await docRef.update({
            [`customerOrg.users.${userId}.teamName`]: team,
            [`customerOrg.users.${userId}.ismsRole`]: ismsRole,
            [`customerOrg.users.${userId}.ismsCommittee`]: ismsCommitteeRole,
        });

        createToast({
            message: 'User roles updated successfully.',
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });
    } catch (error) {
        console.error('Error updating user roles:', error);
        createToast({
            message: 'Failed to update user roles.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}

/**
 * Sets up a real-time listener for user roles using onSnapshot.
 */
export function setupRolesListener(): void {
    waitForCompanyID().then(companyID => {
        const docPath = `/customers/${companyID}`;
        const docRef = db.doc(docPath);

        docRef.onSnapshot(docSnapshot => {
            if (docSnapshot.exists) {
                const users = docSnapshot.data()?.customerOrg?.users;
                if (users && typeof users === 'object') {
                    const roles: UserRole[] = Object.entries(users).map(([userId, user]: [string, any]) => ({
                        userId,
                        teamName: user.teamName || 'N/A',
                        ismsRole: user.ismsRole || 'N/A',
                        ismsCommittee: user.ismsCommittee || 'N/A',
                    }));
                    (window as any).roles = roles;
                    renderRolesTable(roles);
                }
            }
        }, error => {
            console.error('Error with onSnapshot listener:', error);
            createToast({
                message: 'Real-time updates failed.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
        });
    });
}

/**
 * Handles the editing of a user's roles.
 * 
 * @param event - The event triggered by the save button.
 */
export async function handleEditUserRoles(event: Event): Promise<void> {
    event.preventDefault();

    const modal = document.querySelector('[data-managedid="EditRoleModal"]') as HTMLElement;
    const userId = modal.getAttribute('data-user-id');

    if (!userId) {
        console.error('User ID not found in the modal.');
        createToast({
            message: 'An error occurred. Please try again.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }

    // Retrieve updated input values
    const userSelect = document.getElementById('user_edit') as HTMLSelectElement;
    const team = (document.getElementById('team_edit') as HTMLInputElement).value.trim();
    const ismsRole = (document.getElementById('ismsRole_edit') as HTMLSelectElement).value;
    const ismsCommitteeRole = (document.getElementById('ismsCommitteeRole_edit') as HTMLSelectElement).value;

    // Validate inputs
    if (!team || !ismsRole || !ismsCommitteeRole) {
        createToast({
            message: 'All fields are required.',
            customStyles: {
                backgroundColor: '#ffcc00',
                color: '#000000'
            }
        });
        return;
    }

    // Update the user roles
    await updateUserRoles(userId, team, ismsRole, ismsCommitteeRole);

    // Close the modal
    closeModal(modal);
}